<template>
  <v-card :flat="this.$vuetify.breakpoint.mobile">
    <v-card-title>Shearwater Weather Report</v-card-title>
    <v-card-subtitle>Local Time: {{ dateTimeNow }}</v-card-subtitle>
    <div class="px-7 pb-3">
      <WeatherForecastWidget date="now" :iconSize=widgetIconSize :textSize=widgetTextSize></WeatherForecastWidget>
      <v-sparkline :fill=true :padding="1" line-width="3" :smooth="true" :value="weatherForecast.dayWindForcePeaks" auto-draw></v-sparkline>
      <vue-slider class="px-0 py-5" v-model="sliderValue" :marks="sliderDayMarkers" :tooltip-formatter="sliderTooltipValue" :min=0 :max="sliderMax"></vue-slider>
    </div>
    <v-card-actions>
      <v-btn text color="teal accent-4" v-if="!show" @click="show = !show">Show More</v-btn>
      <v-btn text color="teal accent-4" v-if="show" @click="show = !show">Show Less</v-btn>
      <v-spacer></v-spacer>
      <v-btn icon @click="show = !show">
        <v-icon>{{ show ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
      </v-btn>
    </v-card-actions>
    <v-expand-transition>
      <div v-if="show">
        <v-card-subtitle>Forecast Details for {{ sliderDayStr }}</v-card-subtitle>
        <v-simple-table dense>
          <template v-slot:default>
            <thead>
            <tr>
              <th class="text-left">Time</th>
              <th class="text-left">Force</th>
              <th class="text-left">Direction</th>
              <th class="text-left">Visibility</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="weatherInfo in sliderWeatherInfos" :key="weatherInfo.id" :class="{'highlight': (weatherInfo.id === sliderHourId)}">
              <td>{{ weatherInfo.time }}</td>
              <td>{{ weatherInfo.windForce }}</td>
              <td>{{ weatherInfo.windDirection.replace('_', '-') }}</td>
              <td>{{ weatherInfo.visibility }}</td>
            </tr>
            </tbody>
          </template>
        </v-simple-table>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
import _ from 'lodash';
import {eSailServerPublic} from "@/plugins/axios";
import moment from "moment";
import WeatherForecastWidget from "./WeatherForecastWidget";
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/default.css'

export default {
  name: "WeatherCard",
  components: {
    WeatherForecastWidget,
    VueSlider
  },
  data () {
    return {
      show: this.$vuetify.breakpoint.mobile,
      sliderValue: 0,
      dateTimeNow: null,
      tableSelectedRow: [2],
      weatherForecastRaw: null,
      weatherForecast: {
        interval: 3,
        maxDays: 6,
        dayLabels: [],
        dayWindForcePeaks: [],
        daySummaries: []
      }
    }
  },
  computed: {
    widgetIconSize() {
      return this.$vuetify.breakpoint.mobile ? 32 : 48
    },
    widgetTextSize() {
      return this.$vuetify.breakpoint.mobile ? "text-h5" : "text-h4"
    },
    sliderMax() {
      return this.sliderValueHours * this.weatherForecast.maxDays
    },
    sliderValueHours() {
      return 24 / this.weatherForecast.interval
    },
    sliderHourId() {
      return this.sliderValue - this.sliderDayId * this.sliderValueHours
    },
    sliderDayId() {
      return Math.floor(this.sliderValue / this.sliderValueHours)
    },
    sliderDayStr() {
      return this.weatherForecast.daySummaries.length > 0 && this.sliderDayId < this.weatherForecast.daySummaries.length ? this.weatherForecast.daySummaries[this.sliderDayId].date : ""
    },
    sliderWeatherInfos() {
      return this.weatherForecast.daySummaries.length > 0 && this.sliderDayId < this.weatherForecast.daySummaries.length ? this.weatherForecast.daySummaries[this.sliderDayId].items : []
    },
    sliderDayMarkers() {
      return val => val % 8 === 0 ? ({
        label: this.weatherForecast.dayLabels[val / this.sliderValueHours],
        labelActiveStyle: {
          color: '#3498db'
        }
      }) : false
    },
    sliderTooltipValue() {
      return val => this.weatherForecast.dayWindForcePeaks[val < this.sliderMax ? val : this.sliderMax - 1] + " bft"
    }
  },
  mounted() {
    this.dateTimeNow = moment().format('MMMM Do YYYY, hh:mm a')
    this.sliderValue = Math.round(this.sliderValueHours * (moment().hours() + moment().minutes() / 60) / 24)
    eSailServerPublic.get('info/game/environment/weather-forecast?hourInterval=' + this.weatherForecast.interval
        + '&numDays=' + this.weatherForecast.maxDays + '&date=' + encodeURIComponent(moment().format('YYYY-MM-DDTHH:mm:ss.SSSZ')))
        .then(response => {
          let weatherData = response.data
          if (weatherData != null && weatherData.length > 0) {
            this.weatherForecastRaw = response.data
            this.refreshDayLabels()
            this.refreshWindForceArray()
            this.refreshDaySummaries()
          }
        })
  },
  methods: {
    refreshDayLabels() {
      this.weatherForecast.dayLabels = []
      let time = moment()
      for (let i = 0; i <= this.weatherForecast.maxDays; i++) {
        this.weatherForecast.dayLabels.push(time.format('dd').toUpperCase())
        time = time.add(1, 'days')
      }
    },
    refreshWindForceArray() {
      this.weatherForecast.dayWindForcePeaks = _.map(this.weatherForecastRaw, 'windForce')
    },
    refreshDaySummaries() {
      this.weatherForecast.daySummaries = _(this.weatherForecastRaw)
          .groupBy(item => moment(item.date).utc().format('MMMM Do YYYY'))
          .map((items, date) => ({
            date: date,
            items: _.map(items, (item, index) => {
              return {
                id: index,
                time: moment(item.date).utc().format('HH:mm'),
                windForce: item.windForce,
                windDirection: item.windDirection,
                visibility: item.visibility
              }
            })
          })).value()
    }
  }
}
</script>

<style scoped>

.highlight {
  background-color: lightgray;
}
tr:hover{
  cursor: pointer;
}

</style>